import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook that monitors user activity and triggers a callback
 * after a specified period of inactivity.
 *
 * @param onTimeout - Function to call when inactivity timeout is reached
 * @param timeoutMs - Inactivity timeout in milliseconds (default: 30 minutes)
 */
export function useInactivityTimeout(
  onTimeout: () => void,
  timeoutMs: number = 30 * 60 * 1000
): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  // Keep the callback ref up to date without triggering effect re-runs
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'click',
    ];

    // Start the timer immediately
    resetTimer();

    // Reset timer on any user activity
    const handleActivity = () => resetTimer();

    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [resetTimer]);
}
